import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Mail, ArrowRight, CheckCircle2 } from 'lucide-react';

/**
 * HeartsReceiptCelebration
 * ─────────────────────────
 * Lands here after a teen completes a Hearts top-up via Stripe.
 *
 * Designed to do three things at once:
 *  1. Make the teen feel the purchase paid off (floating hearts + +N reveal).
 *  2. Reassure the *parent* with explicit receipt language and the exact
 *     bank-statement descriptor ("HIBFF TEEN COACHING") so the eventual
 *     statement line is recognisable.
 *  3. Drive the teen back into the loop ("Spend your Hearts" CTA → /chat).
 */

const PURPLE = '#7B4FD8';
const ORANGE = '#F5831F';

// Module-level particle field — generated once when the bundle loads, shared
// across mounts. Pure decoration so determinism is not required.
const PARTICLES = Array.from({ length: 14 }).map((_, i) => ({
  id: i,
  left: Math.random() * 100,
  delay: Math.random() * 4,
  duration: 6 + Math.random() * 4,
  scale: 0.5 + Math.random() * 0.9,
  opacity: 0.35 + Math.random() * 0.35,
}));

const HeartsReceiptCelebration = ({
  heartsCredited = 0,
  newBalance = 0,
  parentEmailMasked = null,
  amountTotal = 0,           // pence
  currency = 'GBP',
  statementDescriptor = 'HIBFF TEEN COACHING',
  sessionId,
}) => {
  const navigate = useNavigate();
  const [animatedCount, setAnimatedCount] = useState(0);

  // Count up animation on the +Hearts number — the focal point of the screen.
  useEffect(() => {
    if (heartsCredited <= 0) return;
    const steps = 30;
    const inc = Math.max(1, Math.ceil(heartsCredited / steps));
    let current = 0;
    const t = setInterval(() => {
      current = Math.min(heartsCredited, current + inc);
      setAnimatedCount(current);
      if (current >= heartsCredited) clearInterval(t);
    }, 22);
    return () => clearInterval(t);
  }, [heartsCredited]);

  const amountStr = `${currency.toUpperCase()} ${(amountTotal / 100).toFixed(2)}`;

  return (
    <div
      className="relative max-w-xl mx-auto rounded-3xl overflow-hidden"
      style={{
        background: 'linear-gradient(140deg, #1A0F2E 0%, #2A1A4E 50%, #3D1F5C 100%)',
        boxShadow: '0 24px 80px rgba(123, 79, 216, 0.45), inset 0 1px 0 rgba(255,255,255,0.06)',
        border: `1px solid ${PURPLE}55`,
      }}
      data-testid="hearts-receipt-celebration"
    >
      {/* Decorative grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(circle at 18% 18%, rgba(255, 99, 132, 0.18), transparent 45%), radial-gradient(circle at 82% 78%, rgba(245, 131, 31, 0.14), transparent 50%)',
          pointerEvents: 'none',
        }}
      />

      {/* Floating hearts */}
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
        {PARTICLES.map((p) => (
          <span
            key={p.id}
            className="absolute"
            style={{
              left: `${p.left}%`,
              bottom: '-40px',
              animation: `hibff-heart-rise ${p.duration}s ${p.delay}s ease-in infinite`,
              opacity: p.opacity,
              transform: `scale(${p.scale})`,
            }}
          >
            <Heart size={20} fill="#FF6384" stroke="#FF6384" />
          </span>
        ))}
      </div>

      <div className="relative px-6 sm:px-10 py-10 text-center text-white">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4" style={{ background: 'rgba(46, 170, 92, 0.18)', border: '1px solid rgba(46, 170, 92, 0.5)' }}>
          <CheckCircle2 size={14} style={{ color: '#34D399' }} />
          <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: '#A7F3D0' }}>Payment confirmed</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-extrabold mb-1" data-testid="hearts-celebration-heading">Hearts Unlocked!</h1>
        <p className="text-white/65 text-sm mb-6">Your time credit just landed.</p>

        {/* +N hero number */}
        <div
          className="inline-flex items-center gap-3 px-7 py-5 rounded-2xl mb-6"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 99, 132, 0.2), rgba(245, 131, 31, 0.2))',
            border: `1.5px solid ${ORANGE}66`,
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.08), 0 4px 18px ${ORANGE}33`,
          }}
        >
          <Heart size={36} fill="#FF6384" stroke="#FF6384" />
          <span className="text-5xl sm:text-6xl font-black tabular-nums" style={{ color: '#fff', letterSpacing: '-0.02em' }} data-testid="hearts-credited-count">
            +{animatedCount.toLocaleString()}
          </span>
        </div>

        <p className="text-base text-white/85 mb-1">You now have</p>
        <p className="text-2xl font-bold mb-7" style={{ color: '#FFD8DD' }} data-testid="hearts-new-balance">
          {newBalance.toLocaleString()} Hearts
        </p>

        {/* Parent receipt reassurance card */}
        <div
          className="rounded-2xl p-4 text-left mb-6"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
          data-testid="parent-receipt-card"
        >
          <div className="flex items-start gap-3">
            <div className="rounded-full p-2 flex-shrink-0" style={{ background: `${PURPLE}33` }}>
              <Mail size={18} style={{ color: '#D7BBFF' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-white/95 font-semibold mb-0.5">Receipt emailed to your parent</p>
              <p className="text-[12px] text-white/65 leading-relaxed">
                {parentEmailMasked
                  ? <>Sent to <span className="font-mono text-white/80">{parentEmailMasked}</span></>
                  : <>Sent to your linked parent account</>}
                {' '}— matches their bank statement, which will read{' '}
                <span className="font-mono text-white/85 px-1.5 py-0.5 rounded" style={{ background: 'rgba(255,255,255,0.07)' }}>{statementDescriptor}</span>
                {' '}for {amountStr}.
              </p>
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => navigate('/chat')}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-transform hover:scale-[1.02] active:scale-[0.98]"
            style={{ background: `linear-gradient(135deg, ${ORANGE}, #FFA45A)`, color: '#fff', boxShadow: `0 4px 16px ${ORANGE}55` }}
            data-testid="hearts-spend-cta"
          >
            Spend your Hearts
            <ArrowRight size={16} />
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-colors"
            style={{ background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.18)' }}
            data-testid="hearts-dashboard-cta"
          >
            Back to dashboard
          </button>
        </div>

        {sessionId && (
          <p className="text-[10px] text-white/30 font-mono break-all mt-7" data-testid="hearts-session-id">{sessionId}</p>
        )}
      </div>

      <style>{`
        @keyframes hibff-heart-rise {
          0%   { transform: translateY(0) scale(var(--s,1)) rotate(-4deg); opacity: 0; }
          12%  { opacity: var(--o, 0.6); }
          85%  { opacity: var(--o, 0.6); }
          100% { transform: translateY(-540px) scale(var(--s,1)) rotate(6deg); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="hearts-receipt-celebration"] span[style*="hibff-heart-rise"] { animation: none !important; opacity: 0 !important; }
        }
      `}</style>
    </div>
  );
};

export default HeartsReceiptCelebration;
